/**
 * Small DOM helpers shared by the console views.
 *
 * Everything builds nodes through the DOM rather than innerHTML: app names, avatars and
 * redirect URIs are user-supplied, and Vue was escaping them for free before this port.
 */

export type Attrs = Record<string, string | number | boolean | undefined | null>;

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  children: (Node | string | null | undefined)[] = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);

  for (const [key, value] of Object.entries(attrs)) {
    if (value === undefined || value === null || value === false) continue;
    if (key === 'class') node.className = String(value);
    else if (key === 'text') node.textContent = String(value);
    else if (value === true) node.setAttribute(key, '');
    else node.setAttribute(key, String(value));
  }

  for (const child of children) {
    if (child === null || child === undefined) continue;
    node.append(typeof child === 'string' ? document.createTextNode(child) : child);
  }

  return node;
}

/** Inline SVG icons, kept as a map so views stay readable. */
const ICON_PATHS: Record<string, string> = {
  plus: 'M10 4.5v11M4.5 10h11',
  trash: 'M4 6h12M8.5 6V4.5h3V6M6 6l.8 10h6.4L14 6',
  copy: 'M7.5 7.5h8v8h-8zM12.5 7.5V4.5h-8v8h3',
  upload: 'M10 13.5v-9M6.5 8 10 4.5 13.5 8M4.5 13v2.5h11V13',
  external: 'M8 4.5H4.5v11h11V12M11 4.5h4.5V9M15.5 4.5 9 11',
};

export function icon(name: keyof typeof ICON_PATHS | string, size = 15): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 20 20');
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');

  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', ICON_PATHS[name] ?? '');
  path.setAttribute('fill', 'none');
  path.setAttribute('stroke', 'currentColor');
  path.setAttribute('stroke-width', '1.6');
  path.setAttribute('stroke-linecap', 'round');
  path.setAttribute('stroke-linejoin', 'round');
  svg.append(path);

  return svg;
}

export function clear(node: Element): void {
  while (node.firstChild) node.firstChild.remove();
}

/* -------------------------------------------------------------------------- toasts */

let toastHost: HTMLElement | null = null;

export function toast(message: string, tone: 'ok' | 'error' = 'ok'): void {
  if (!toastHost) {
    toastHost = el('div', { class: 'toast-host', role: 'status', 'aria-live': 'polite' });
    document.body.append(toastHost);
  }

  const node = el('div', { class: `toast is-${tone}`, text: message });
  toastHost.append(node);

  window.setTimeout(() => {
    node.classList.add('is-leaving');
    window.setTimeout(() => node.remove(), 220);
  }, 4200);
}

/* -------------------------------------------------------------------------- modals */

export interface ModalOptions {
  title: string;
  body: Node;
  confirmLabel: string;
  cancelLabel?: string;
  tone?: 'default' | 'danger';
  /** Return false to keep the modal open (validation failed). */
  onConfirm: () => boolean | void | Promise<boolean | void>;
}

/**
 * A focus-trapping dialog. Escape and the backdrop close it, focus returns to whatever
 * opened it, and the confirm button locks while an async handler runs.
 */
export function openModal(options: ModalOptions): () => void {
  const opener = document.activeElement as HTMLElement | null;

  const cancel = el('button', { class: 'btn', type: 'button', text: options.cancelLabel ?? 'Cancel' });
  const confirm = el('button', {
    class: `btn ${options.tone === 'danger' ? 'btn-danger-solid' : 'btn-primary'}`,
    type: 'button',
    text: options.confirmLabel,
  });

  const dialog = el(
    'div',
    { class: 'modal', role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': 'modal-title' },
    [
      el('div', { class: 'modal-head' }, [
        el('h2', { id: 'modal-title', class: 'modal-title', text: options.title }),
      ]),
      el('div', { class: 'modal-body' }, [options.body]),
      el('div', { class: 'modal-foot' }, [cancel, confirm]),
    ],
  );

  const backdrop = el('div', { class: 'modal-backdrop' }, [dialog]);

  const close = () => {
    document.removeEventListener('keydown', onKey, true);
    backdrop.remove();
    document.body.classList.remove('modal-open');
    opener?.focus?.();
  };

  const onKey = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = [
      ...dialog.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      ),
    ].filter((node) => !node.hasAttribute('disabled'));
    if (!focusable.length) return;

    const first = focusable[0] as HTMLElement;
    const last = focusable[focusable.length - 1] as HTMLElement;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  cancel.addEventListener('click', close);
  backdrop.addEventListener('mousedown', (event) => {
    if (event.target === backdrop) close();
  });
  document.addEventListener('keydown', onKey, true);

  confirm.addEventListener('click', async () => {
    confirm.disabled = true;
    confirm.classList.add('is-busy');
    try {
      const result = await options.onConfirm();
      if (result !== false) close();
    } finally {
      confirm.disabled = false;
      confirm.classList.remove('is-busy');
    }
  });

  document.body.classList.add('modal-open');
  document.body.append(backdrop);

  const firstField = dialog.querySelector<HTMLElement>('input, textarea, select');
  (firstField ?? confirm).focus();

  return close;
}

export function confirmDialog(options: {
  title: string;
  message: string;
  confirmLabel: string;
  onConfirm: () => boolean | void | Promise<boolean | void>;
}): void {
  openModal({
    title: options.title,
    body: el('p', { class: 'modal-text', text: options.message }),
    confirmLabel: options.confirmLabel,
    tone: 'danger',
    onConfirm: options.onConfirm,
  });
}

/* -------------------------------------------------------------------------- fields */

export interface FieldOptions {
  label: string;
  id: string;
  value?: string;
  type?: string;
  hint?: string;
  placeholder?: string;
  readonly?: boolean;
  autocomplete?: string;
}

export function field(options: FieldOptions): { wrap: HTMLElement; input: HTMLInputElement } {
  const input = el('input', {
    id: options.id,
    class: 'field',
    type: options.type ?? 'text',
    value: options.value ?? '',
    placeholder: options.placeholder,
    readonly: options.readonly,
    autocomplete: options.autocomplete ?? 'off',
    spellcheck: 'false',
  });
  // `value` as an attribute only seeds the default; set the property so later reads are right.
  input.value = options.value ?? '';

  const wrap = el('div', { class: 'field-row' }, [
    el('label', { class: 'field-label', for: options.id, text: options.label }),
    input,
    options.hint ? el('p', { class: 'field-hint', text: options.hint }) : null,
  ]);

  return { wrap, input };
}

/**
 * Row of small buttons pinned to a field's label line (Copy, Show…). One flex container
 * rather than absolutely placed siblings, so a third button never needs a magic offset.
 */
export function fieldActions(wrap: HTMLElement, buttons: HTMLElement[]): void {
  if (!buttons.length) return;
  wrap.classList.add('has-action');
  wrap.append(el('div', { class: 'field-actions' }, buttons));
}

export function errorState(message: string, onRetry?: () => void): HTMLElement {
  return el('div', { class: 'console-empty is-error' }, [
    el('p', { class: 'console-empty-title', text: 'Something went wrong' }),
    el('p', { class: 'console-empty-note', text: message }),
    onRetry
      ? (() => {
          const button = el('button', { class: 'btn', type: 'button', text: 'Try again' });
          button.addEventListener('click', onRetry);
          return button;
        })()
      : null,
  ]);
}
